import DateTimeDisplay from '@/components/DateTime';
import { formatITCModifierData } from '@/components/Pricing/ITCModifiers/SpecificITCModifier';
import { Box, Button, Column, Table } from '@palmetto/palmetto-components';
import { memo, useMemo, useCallback } from 'react';

import { DomesticContentSetting, ItcModifierSystemType } from 'types';

import type { DomesticContentModifier } from './types';

interface ActiveDomesticContentTableProps {
  modifiers: DomesticContentModifier[];
  isLoading: boolean;
  onEdit: (modifier: DomesticContentModifier) => void;
  canEdit?: boolean;
}

export const ActiveDomesticContentTable = memo(
  ({ modifiers, isLoading, onEdit, canEdit = true }: ActiveDomesticContentTableProps) => {
    const renderPricingColumn = useCallback((row: DomesticContentModifier, systemType: ItcModifierSystemType) => {
      const setting = row?.values?.find((d: DomesticContentSetting) => d.systemType === systemType);
      if (!setting?.modifier) return '---';

      const netValue = setting.modifier.value - setting.holdback.value;
      const net = formatITCModifierData({
        ...setting.modifier,
        value: netValue,
      });

      return (
        <Box childGap="xs">
          <Box direction="row" childGap="xs">
            <Box fontWeight="bold">Modifier:</Box> {formatITCModifierData(setting.modifier)}
          </Box>
          <Box direction="row" childGap="xs">
            <Box fontWeight="bold">Holdback:</Box> {formatITCModifierData(setting.holdback)}
          </Box>
          <Box direction="row" childGap="xs">
            <Box fontWeight="bold">Net:</Box> {net}
          </Box>
        </Box>
      );
    }, []);

    const columns = useMemo(
      () => [
        {
          heading: 'State',
          dataKey: 'state',
          render: (cell: string) => cell || '---',
        },
        {
          heading: 'Org Name',
          dataKey: 'organizationName',
          render: (cell: string) => cell || '---',
        },
        {
          heading: 'PV',
          render: (_cell: unknown, row: DomesticContentModifier) =>
            renderPricingColumn(row, ItcModifierSystemType.pvOnly),
        },
        {
          heading: 'Storage',
          render: (_cell: unknown, row: DomesticContentModifier) =>
            renderPricingColumn(row, ItcModifierSystemType.storageOnly),
        },
        {
          heading: 'PV & Storage',
          render: (_cell: unknown, row: DomesticContentModifier) =>
            renderPricingColumn(row, ItcModifierSystemType.pvAndStorage),
        },
        {
          heading: 'Created',
          render: (_cell: unknown, row: DomesticContentModifier) => {
            const dateValue = row.createdBy?.date ?? row.createdDate;
            return dateValue ? <DateTimeDisplay value={dateValue} dateFormat="DD h:mm:ss a" /> : '---';
          },
        },
        {
          heading: 'Created By',
          render: (_cell: unknown, row: DomesticContentModifier) => row.createdBy?.user?.name ?? '---',
        },
        {
          render: (_cell: unknown, row: DomesticContentModifier) => (
            <Button
              variant="secondary"
              iconPrefix="pencil"
              size="sm"
              tone="neutral"
              onClick={() => onEdit(row)}
              aria-label="Edit domestic content"
              isDisabled={!canEdit}
            />
          ),
        },
      ],
      [renderPricingColumn, onEdit, canEdit],
    );

    if (!isLoading && modifiers.length === 0) {
      return (
        <Box padding="lg" textAlign="center" color="grey-500">
          No active settings
        </Box>
      );
    }

    return (
      <Table
        rowKey="id"
        columns={columns as Column[]}
        rows={modifiers}
        align="left"
        isScrollable={{ x: false, y: true }}
        isLoading={isLoading}
      />
    );
  },
);

ActiveDomesticContentTable.displayName = 'ActiveDomesticContentTable';
